import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { CoverageOptions } from 'src/configs/APISchemas/Application.schema';
import { ContactSupportId } from 'src/constants/constants';
import { handleTgOptionalButStateMandatoryCoverages } from 'src/pages/CoveragesDetails/utils/handle-mandatory-state-coverages.util';
import {
  removeAutoProcessFromStorage,
  saveAutoProcessToStorage,
} from 'src/storage/auto-process.storage';
import { FormGroupProps } from '../../components/FormComponents/Components';
import {
  CREATE_APPLICATION,
  CreateApplicationResponse,
} from '../../configs/APISchemas/CreateApplication';
import {
  CREATE_COMMUNITY,
  CreateCommunityResponse,
} from '../../configs/APISchemas/CreateCommunity';
import {
  GET_BUILDING,
  GetBuildingResponse,
} from '../../configs/APISchemas/GetBuilding';
import { GET_QUOTE, GetQuoteResponse } from '../../configs/APISchemas/GetQuote';
import {
  PRODUCT_DEFINITION,
  ProductDefinitionResponse,
} from '../../configs/APISchemas/ProductDefinition';
import {
  UPDATE_APPLICATION,
  UpdateApplicationAdditionalData,
  UpdateApplicationResponse,
} from '../../configs/APISchemas/UpdateApplication';
import { CoverageType } from '../../constants/coverage-type';
import { FeatureFlag } from '../../constants/feature-flag';
import { ROUTES } from '../../constants/routes';
import {
  STATE_LOSS_OF_USE_RATE,
  defaultLossOfUseRate,
} from '../../constants/states';
import { usePrefillContext } from '../../context/prefill-context';
import { CoveragesDetailsForm } from '../../pages/CoveragesDetails/utils/coverages-details-form.util';
import {
  updatePurchasePolicyAdditionalData,
  updatePurchasePolicyInput,
} from '../../redux/actions/purchasePolicy';
import { updateUtmSource } from '../../redux/actions/system';
import { updateVerifyPolicyInput } from '../../redux/actions/verifyPolicy';
import { PurchasePolicyInput } from '../../redux/reducer/v2/purchasePolicy';
import { getPurchasePolicyInput } from '../../redux/selectors/purchasePolicy';
import { getUtmSource } from '../../redux/selectors/system';
import { logToBackend } from '../BackendLogger';
import { GraphQLRequest } from '../GraphQLRequest';
import { separateAddressNumberAndAddressStreet } from '../building';
import {
  COMPLIANCE_SHOW_UNIT_AUTOCOMPLETE_KEY,
  REGULAR_SHOW_UNIT_AUTOCOMPLETE_KEY,
  saveShowUnitAutocompleteToStorage,
} from '../hooks/UnitField/useUnitFieldVisibility';
import useGetFeatureFlag from '../hooks/useGetFeatureFlag';
import { useLocationSearch } from '../hooks/useLocationSearch';
import { omit } from '../index';
import {
  AllowedKeys,
  AllowedKeysReturnByKey,
  CoverageValues,
  GetBuilding,
  InitialValues,
  ProductDefinitionsData,
  QuoteInformationValues,
  StartApplicationRequestReturn,
} from './types';
import { generateErrorMessage } from './utils/generate-error-message.util';
import { getCreateCommunityInput } from './utils/get-create-community-input.util';
import { parseQuery } from './utils/parse-query.util';

export const useParseFunction = () => {
  const location = useLocation();
  const params = useParams<{ id: string }>();

  return useCallback(() => {
    const updateData: { [key in AllowedKeys]?: AllowedKeysReturnByKey[key] } =
      {};
    if (params?.id) {
      updateData.buildingId = params?.id;
    }

    if (!location.search) {
      return updateData;
    }

    const parsed = parseQuery(location.search);
    if (parsed.firstName && parsed.lastName && parsed.email) {
      saveAutoProcessToStorage(true);
    } else {
      removeAutoProcessFromStorage();
    }
    if (parsed.firstName) {
      updateData.firstName = parsed.firstName;
    }
    if (parsed.lastName) {
      updateData.lastName = parsed.lastName;
    }
    if (parsed.email) {
      updateData.email = parsed.email;
    }
    if (parsed.phoneNumber) {
      updateData.phone = parsed.phoneNumber
        ? parsed.phoneNumber.replace('+1', '')
        : '';
    }

    const leaseStart = parsed.leaseStart ? moment(parsed.leaseStart, 'MM/DD/YYYY') : null;
    const leaseEnd = parsed.leaseEnd ? moment(parsed.leaseEnd, 'MM/DD/YYYY') : null;
    const tomorrow = moment().add(1, 'day').startOf('day');

    if (leaseStart && leaseStart.isValid() && leaseStart.isAfter(tomorrow)) {
      updateData.coverageStart = leaseStart.format('YYYY-MM-DD');
      updateData.leaseStart = leaseStart.format('YYYY-MM-DD');
    } else {
      updateData.coverageStart = tomorrow.format('YYYY-MM-DD');
      updateData.leaseStart = tomorrow.format('YYYY-MM-DD');
    }

    if (leaseEnd && leaseEnd.isValid() && leaseEnd.isAfter(tomorrow)) {
      updateData.leaseEnd = leaseEnd.format('YYYY-MM-DD');
    } else {
      updateData.leaseEnd = tomorrow.format('YYYY-MM-DD');
    }

    if (parsed.addressUnit) {
      if (!location.pathname.includes('compliance')) {
        saveShowUnitAutocompleteToStorage(
          REGULAR_SHOW_UNIT_AUTOCOMPLETE_KEY,
          false,
        );
      }

      if (location.pathname.includes('compliance')) {
        saveShowUnitAutocompleteToStorage(
          COMPLIANCE_SHOW_UNIT_AUTOCOMPLETE_KEY,
          false,
        );
      }

      updateData.buildingUnit = parsed.addressUnit;
    }

    if (location?.pathname?.includes('compliance')) {
      if (parsed.buildingId) {
        updateData.buildingId = parsed.buildingId;
      }
      if (parsed.buildingUnit) {
        updateData.buildingUnit = parsed.buildingUnit;
      }
    }

    if (
      parsed.roommateFirstName &&
      parsed.roommateLastName &&
      parsed.roommateEmail
    ) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updateData['additionalInsuredRoommates'] = [
        {
          firstName: parsed.roommateFirstName,
          lastName: parsed.roommateLastName,
          email: parsed.roommateEmail,
        },
      ];
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updateData['additionalInsuredRoommates'].RadioButtonGroup = 'yes';
    }

    return updateData;
  }, [location.pathname, location.search, params?.id]);
};

export const usePrefillApplication = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const parseFunction = useParseFunction();

  const setUtmSource = useCallback(async () => {
    return new Promise((resolve) => {
      if (process.env.NODE_ENV === 'test') {
        return resolve(true);
      }
      const parsed = parseQuery(location.search);
      const utmSource = parsed['source'] || parsed['utm_source'];
      if (utmSource) {
        dispatch(updateUtmSource(utmSource));
      }
      return resolve(true);
    });
  }, [dispatch, location.search]);

  const setPrefillData = useCallback(() => {
    return new Promise((resolve) => {
      const updateData = parseFunction();
      if (Object.keys(updateData).length) {
        dispatch(
          updatePurchasePolicyInput(
            omit(updateData, ['leaseStart', 'leaseEnd']),
          ),
        );
        dispatch(
          updateVerifyPolicyInput(
            omit(updateData, ['additionalInsuredRoommates', 'coverageStart']),
          ),
        );
      }
      return resolve(true);
    });
  }, [dispatch, parseFunction]);

  useEffect(() => {
    (async () => {
      await setUtmSource();
      await setPrefillData();
      // clear location.search from url
      if (location.search) {
        navigate(location.pathname);
      }
    })();
    // eslint-disable-next-line
  }, [setPrefillData, setUtmSource]);
};

export const useAutoProcess = () => {
  const purchasePolicyInput = useSelector(getPurchasePolicyInput);
  const utmSource = useSelector(getUtmSource);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getUrlWithQueryParams = useLocationSearch();
  const { isLoading, isCalled, setIsLoading, setIsCalled } =
    usePrefillContext();
  const { getFeatureFlag } = useGetFeatureFlag();

  const getBuilding = useCallback(async (): Promise<GetBuilding | void> => {
    if (purchasePolicyInput.buildingId) {
      const query = GET_BUILDING(purchasePolicyInput.buildingId);
      const response = await GraphQLRequest<GetBuildingResponse>(query);
      if (response?.building) {
        return {
          name: response.building.name,
          street: response.building.fullAddress.street,
          municipality: response.building.fullAddress.municipality,
          state: response.building.fullAddress.region,
          postalCode: response.building.fullAddress.postalCode,
          complete_address: response.building.name,
          fullAddress: response.building.fullAddress,
        };
      } else {
        throw new Error('Building not found');
      }
    }
  }, [purchasePolicyInput]);

  const isEligibleToAutoProcessStartApplication = useMemo(() => {
    return (
      purchasePolicyInput.firstName &&
      purchasePolicyInput.lastName &&
      purchasePolicyInput.email &&
      purchasePolicyInput.buildingId
    );
  }, [purchasePolicyInput]);

  const startApplicationRequest = useCallback(
    async (
      _values: InitialValues,
    ): Promise<StartApplicationRequestReturn | void> => {
      const building = await getBuilding();
      if (!building) {
        return;
      }

      const [addressNumber, street] =
        separateAddressNumberAndAddressStreet(building);

      const values: InitialValues & {
        buildingName: string;
        address: PurchasePolicyInput['address'];
      } = {
        ..._values,
        buildingName: building.name,
        fixedBuilding: true,
        addressName: `${addressNumber} ${street}, ${building.fullAddress.municipality}, ${building.fullAddress.region}, USA`,
        address: {
          street: street,
          addressNumber: addressNumber,
          shortNameStreet: street,
          municipality: building.fullAddress.municipality,
          postalCode: building.fullAddress.postalCode,
          state: building.fullAddress.region,
        },
      };

      const mutation = CREATE_APPLICATION({
        application: values,
        utmSource,
      });
      const response = await GraphQLRequest<CreateApplicationResponse>(
        mutation,
      );

      if (response.createApplication) {
        if ('errors' in response.createApplication) {
          throw new Error(response.createApplication.errors[0].message);
        } else {
          dispatch(
            updatePurchasePolicyInput({
              ...values,
              buildingId: response.createApplication.buildingId,
            }),
          );
          dispatch(
            updatePurchasePolicyAdditionalData({
              id: response.createApplication.id,
            }),
          );
          navigate(getUrlWithQueryParams(ROUTES.QUOTE_INFORMATION_PATH));

          const additionalInsuredRoommates =
            (values['additionalInsuredRoommates']?.length || 0) > 0
              ? {
                  additionalInsuredRoommates: values.additionalInsuredRoommates,
                }
              : {};

          const [addressNumber, street] =
            separateAddressNumberAndAddressStreet(building);

          const result: CoverageValues = {
            ...values,
            id: response.createApplication.id,
            buildingId: response.createApplication.buildingId,
            companyVendor: null,
            buildingAddress: {
              value: {
                ...building,
                addressNumber,
                street,
              },
            },
            ...additionalInsuredRoommates,
          };

          dispatch(
            updatePurchasePolicyInput(omit(result, ['buildingAddress', 'id'])),
          );

          return {
            coverageValues: result,
          };
        }
      }
    },
    [getBuilding, utmSource, dispatch, getUrlWithQueryParams, navigate],
  );

  const quoteInformationRequest = useCallback(
    async (data: CoverageValues): Promise<void | QuoteInformationValues> => {
      const buildingDetails = data.buildingAddress?.value;
      if (!buildingDetails) return;

      const createCommunityInput = getCreateCommunityInput(
        data,
        buildingDetails,
      );

      const communityMutation = CREATE_COMMUNITY(createCommunityInput, data.id);
      const communityResponse = await GraphQLRequest<CreateCommunityResponse>(
        communityMutation,
      );
      if (communityResponse.createCommunity) {
        if ('errors' in communityResponse.createCommunity) {
          throw new Error(
            generateErrorMessage(
              communityResponse.createCommunity.errors[0].message,
            ),
          );
        }
      }

      const updateApplicationAdditionalData: UpdateApplicationAdditionalData = {
        communityId: communityResponse.createCommunity.communityId || null,
        id: data.id,
      };

      const mutation = UPDATE_APPLICATION(
        omit(data, ['id']),
        updateApplicationAdditionalData,
      );
      const response = await GraphQLRequest<UpdateApplicationResponse>(
        mutation,
      );
      if (response.updateApplication) {
        if ('errors' in response.updateApplication) {
          throw new Error(response.updateApplication.errors[0].message);
        } else {
          const applicationData = {
            ...data,
            ...response.updateApplication,
            ...communityResponse.createCommunity,
          };
          dispatch(
            updatePurchasePolicyInput(
              omit(applicationData, ['id', 'buildingAddress', 'communityId']),
            ),
          );

          dispatch(
            updatePurchasePolicyAdditionalData({
              id: response.updateApplication.id,
              communityId: communityResponse.createCommunity.communityId,
            }),
          );
          navigate(getUrlWithQueryParams(ROUTES.COVERAGES_DETAIL_PATH));
          return applicationData;
        }
      } else {
        throw new Error('Error updating application');
      }
    },
    [dispatch, getUrlWithQueryParams, navigate],
  );

  const getProductDefinitions = useCallback(
    async (data: QuoteInformationValues) => {
      if (!data) return;
      try {
        const query = PRODUCT_DEFINITION({
          id: data.id,
          coverageStart: data.coverageStart,
          state: data.buildingAddress.value.state,
        });
        const response = await GraphQLRequest<ProductDefinitionResponse>(query);

        if (response.productDefinition) {
          if ('errors' in response.productDefinition) {
            throw new Error(response.productDefinition.errors[0].message);
          } else {
            const updatedOptionalCoverages =
              handleTgOptionalButStateMandatoryCoverages(
                response.productDefinition.productCoverageList,
              );
            const applicationData: ProductDefinitionsData = {
              ...data,
              underwritingCompanyId:
                response.productDefinition.underwritingCompanyId,
              underwritingCompanyName:
                response.productDefinition.underwritingCompanyName,
              optionalCoverages: updatedOptionalCoverages,
              lossOfUse: 0,
              personalPropertyCoverage: 0,
              personalLiabilityCoverage: 0,
              medicalPayments: 0,
              deductible: 0,
              heading: 0,
              waterBackup: 0,
              petDamage: 0,
              theft: 0,
              identityFraud: 0,
              windHail: 0,
              earthquake: 0,
              hurricane: 0,
            };

            const formProps = CoveragesDetailsForm(
              response.productDefinition,
              applicationData,
            );

            const coverageItems = (
              formProps.formItems[0].field.templateOptions as FormGroupProps
            ).items;

            if (!coverageItems) {
              throw new Error("Couldn't find any coverages");
            }

            coverageItems.forEach((item) => {
              const key = item.key as CoverageType;
              if (key === 'lossOfUse') {
                const personalPropertyCoverage = coverageItems.find(
                  (i) => i.key === 'personalPropertyCoverage',
                )?.templateOptions as any;
                const state = data.buildingAddress.value.state;
                const RATE =
                  STATE_LOSS_OF_USE_RATE[state] ?? defaultLossOfUseRate;
                if (personalPropertyCoverage) {
                  applicationData[key] =
                    personalPropertyCoverage.initalValue * RATE;
                }
              }
              if ((item.templateOptions as any).initalValue) {
                applicationData[key] = (
                  item.templateOptions as any
                ).initalValue;
              }
            });

            const coverageOptions = coverageItems.reduce((acc, item) => {
              if (!(item.key in CoverageType)) {
                return acc;
              }
              acc[item.key as CoverageType] =
                (item.templateOptions as any).optional || false;
              return acc;
            }, {} as CoverageOptions);

            dispatch(
              updatePurchasePolicyInput({
                ...omit(applicationData, [
                  'underwritingCompanyId',
                  'underwritingCompanyName',
                ]),
              }),
            );
            dispatch(
              updatePurchasePolicyAdditionalData({
                coverageOptions,
                ...response.productDefinition,
              }),
            );

            return applicationData;
          }
        }
      } catch (error: any) {
        if (error?.response?.data?.errors) {
          throw new Error(error.response.data.errors[0].message);
        }
        if (typeof error === 'string') {
          throw new Error(error);
        }
        await logToBackend(error.toString());
        throw new Error(
          `We are having some trouble, Please contact support for help. id: ${ContactSupportId.covie}`,
        );
      }
    },
    [dispatch],
  );

  const getQuote = useCallback(
    async (values: ProductDefinitionsData) => {
      const quoteMutation = GET_QUOTE({
        id: values.id,
        communityId: values.communityId?.toString() || '',
        deductible: values.deductible?.toString() || '',
        medicalPayments: values.medicalPayments?.toString() || '',
        personalPropertyCoverage:
          values.personalPropertyCoverage?.toString() || '',
        personalLiabilityCoverage:
          values.personalLiabilityCoverage?.toString() || '',
        lossOfUse: values.lossOfUse?.toString() || '',
        theft: values.theft?.toString() || '',
        windHail: values.windHail?.toString() || '',
        waterBackup: values.waterBackup?.toString() || '',
        petDamage: values.petDamage?.toString() || '',
        identityFraud: values.identityFraud?.toString() || '',
        fireDepartmentService:
          values.optionalCoverages?.fireDepartmentService ?? '',
        sinkHole: values.optionalCoverages?.sinkHole ?? '',
        ordinanceOrLaw: values.optionalCoverages?.ordinanceOrLaw ?? '',
        rentalIncome: values.optionalCoverages?.rentalIncome ?? '',
        workersCompensation:
          values.optionalCoverages?.workersCompensation ?? '',
        earthquake: values.earthquake,
        hurricane: values.hurricane,
      });

      const quoteResponse = await GraphQLRequest<GetQuoteResponse>(
        quoteMutation,
        {
          typeOfQuery: 'GET_QUOTE',
        },
      );

      const quoteResponseData = quoteResponse?.getQuote;

      if (!quoteResponseData) {
        throw Error('Application Not Created');
      }
      if ('errors' in quoteResponseData) {
        const errorMessage = quoteResponseData.errors[0]?.message;
        toast(errorMessage, {
          type: 'error',
        });
        return;
      }

      dispatch(
        updatePurchasePolicyAdditionalData({
          id: quoteResponseData.id,
          companyProductCd: quoteResponseData.companyProductCd,
          paymentPlans: quoteResponseData.paymentPlans,
        }),
      );
    },
    [dispatch],
  );

  const initialValues = useMemo<InitialValues>(
    () => ({
      firstName: purchasePolicyInput.firstName,
      lastName: purchasePolicyInput.lastName,
      email: purchasePolicyInput.email,
      phone: purchasePolicyInput.phone,
      addressName: '',
      coverageStart: purchasePolicyInput.coverageStart,
      additionalInsuredRoommates:
        purchasePolicyInput.additionalInsuredRoommates,
      buildingId: purchasePolicyInput.buildingId,
      buildingUnit: purchasePolicyInput.buildingUnit,
      fixedUnit: Boolean(purchasePolicyInput.buildingUnit),
    }),
    [
      purchasePolicyInput.additionalInsuredRoommates,
      purchasePolicyInput.buildingId,
      purchasePolicyInput.buildingUnit,
      purchasePolicyInput.coverageStart,
      purchasePolicyInput.email,
      purchasePolicyInput.firstName,
      purchasePolicyInput.lastName,
      purchasePolicyInput.phone,
    ],
  );

  const autoProcessStartApplication = useCallback(async () => {
    try {
      if (!isEligibleToAutoProcessStartApplication) {
        throw new Error('Not eligible to auto process start application');
      }

      setIsLoading(true);
      setIsCalled(true);
      const startApplicationResponse = await startApplicationRequest(
        initialValues,
      );
      if (!startApplicationResponse) {
        throw new Error('Error creating application');
      }
      const quoteValues = await quoteInformationRequest(
        startApplicationResponse.coverageValues,
      );
      if (!quoteValues) {
        throw new Error('Error creating application');
      }
      const productDefinitionValues = await getProductDefinitions(quoteValues);
      if (!productDefinitionValues) {
        throw new Error('Error creating application');
      }
      await getQuote(productDefinitionValues);

      const isCrossSellPaymentPageEnabled = await getFeatureFlag(
        FeatureFlag.crossSellPaymentPage,
      );

      navigate(
        isCrossSellPaymentPageEnabled
          ? ROUTES.PAYMENT_CROSS_SELL_PATH
          : ROUTES.PAYMENT_PATH,
      );

      setIsLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('something went wrong', (e as Error)?.message);
      // eslint-disable-next-line no-console
      console.error(e);
      setIsLoading(false);
      setIsCalled(true);
    }
    // quote information
  }, [
    getQuote,
    isEligibleToAutoProcessStartApplication,
    setIsLoading,
    setIsCalled,
    startApplicationRequest,
    initialValues,
    quoteInformationRequest,
    getProductDefinitions,
    getFeatureFlag,
    navigate,
  ]);

  useEffect(() => {
    void autoProcessStartApplication();
  }, [autoProcessStartApplication]);

  return useMemo(
    () => ({
      isLoading,
      isCalled,
    }),
    [isLoading, isCalled],
  );
};
